import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, CheckCircle, XCircle, Clock, TrendingUp, X, Image } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { useBranchStore } from '../../stores/branch.store';
import { useWasteEntries, useWasteSummary, WasteEntry } from '../../api/queries/waste.queries';
import RecordWasteModal from './components/RecordWasteModal';
import { apiClient } from '../../api/client';

function WastePage() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedEntry, setSelectedEntry] = useState<WasteEntry | null>(null);

  // Use selectedBranchId for HQ users, user's branchId for branch users
  const branchId = selectedBranchId || user?.branchId;

  const { data: wasteEntries, isLoading, refetch } = useWasteEntries(branchId, statusFilter);
  const { data: summary } = useWasteSummary(branchId);

  const handleApprove = async (entryId: string, approved: boolean) => {
    try {
      await apiClient.patch(`/waste/${entryId}/approve`, { approved });
      // Refresh the list
      refetch();
    } catch (error) {
      console.error('Failed to approve/reject waste:', error);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'expired':
        return 'bg-red-100 text-red-800 dark:bg-red-950/20 dark:text-red-200';
      case 'damaged':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-950/20 dark:text-orange-200';
      case 'spillage':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-200';
      case 'quality_issue':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-200';
      case 'over_production':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-950/20 dark:text-gray-200';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-950/20 dark:text-green-200">
          <CheckCircle className="h-3 w-3" /> {t('waste.approved')}
        </span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-950/20 dark:text-red-200">
          <XCircle className="h-3 w-3" /> {t('waste.rejected')}
        </span>;
      default:
        return <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 dark:bg-orange-950/20 dark:text-orange-200">
          <Clock className="h-3 w-3" /> {t('waste.pending')}
        </span>;
    }
  };

  const canApprove = user?.role === 'BRANCH_SUPERVISOR' || user?.role === 'BRANCH_MANAGER' || user?.role === 'HQ_ADMIN';

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('waste.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('waste.subtitle') || 'Track and manage inventory waste'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Filter dropdown */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('common.all')}</option>
              <option value="pending">{t('waste.pending')}</option>
              <option value="approved">{t('waste.approved')}</option>
              <option value="rejected">{t('waste.rejected')}</option>
            </select>
            <button
              onClick={() => setShowRecordModal(true)}
              className="rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              {t('waste.record')}
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="border-b bg-card p-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg bg-secondary p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs">{t('waste.totalValue') || 'Total Value'}</span>
              </div>
              <div className="text-2xl font-bold">฿{summary.totalValue.toLocaleString()}</div>
            </div>
            <div className="rounded-lg bg-secondary p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <span className="text-xs">{t('waste.totalEntries') || 'Total Entries'}</span>
              </div>
              <div className="text-2xl font-bold">{summary.totalEntries}</div>
            </div>
            <div className="rounded-lg bg-secondary p-4">
              <div className="text-xs text-muted-foreground mb-2">{t('waste.byCategory') || 'By Category'}</div>
              <div className="space-y-1 text-xs">
                {Object.entries(summary.byCategory || {}).map(([cat, data]) => (
                  <div key={cat} className="flex justify-between">
                    <span className="capitalize">{t(`waste.${cat}`) || cat}</span>
                    <span className="font-bold">฿{data.value.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Waste Entries Table */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Clock className="h-12 w-12 animate-pulse text-muted-foreground" />
          </div>
        ) : wasteEntries && wasteEntries.length > 0 ? (
          <div className="rounded-lg border bg-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3 font-medium">{t('inventory.ingredient')}</th>
                  <th className="text-right p-3 font-medium">{t('inventory.quantity')}</th>
                  <th className="text-center p-3 font-medium">{t('waste.category')}</th>
                  <th className="text-left p-3 font-medium">{t('inventory.reason')}</th>
                  <th className="text-right p-3 font-medium">{t('waste.value') || 'Value'}</th>
                  <th className="text-center p-3 font-medium">{t('inventory.status')}</th>
                  {canApprove && <th className="text-center p-3 font-medium">{t('common.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {wasteEntries.map((entry) => (
                  <tr
                    key={entry._id}
                    className="border-t hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <td className="p-3">
                      <div className="font-medium">{entry.ingredientName}</div>
                      <div className="text-xs text-muted-foreground">
                        by {entry.recordedByName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="font-bold">{entry.quantity.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">{entry.unit}</div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getCategoryColor(entry.category)}`}>
                        {t(`waste.${entry.category}`)}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="text-sm max-w-xs truncate" title={entry.reason}>
                        {entry.reason}
                      </div>
                      {entry.photoUrl && (
                        <a
                          href={entry.photoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          📷 {t('waste.viewPhoto') || 'View photo'}
                        </a>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="font-bold text-red-600">
                        ฿{entry.estimatedValue.toFixed(2)}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {getStatusBadge(entry.status)}
                    </td>
                    {canApprove && (
                      <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        {entry.status === 'pending' && (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleApprove(entry._id, true)}
                              className="rounded border bg-green-50 px-2 py-1 text-xs text-green-700 hover:bg-green-100 dark:bg-green-950/20 dark:text-green-200"
                            >
                              {t('waste.approve')}
                            </button>
                            <button
                              onClick={() => handleApprove(entry._id, false)}
                              className="rounded border bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100 dark:bg-red-950/20 dark:text-red-200"
                            >
                              {t('waste.reject')}
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <XCircle className="h-16 w-16 mb-4 opacity-20" />
            <p>{t('waste.noEntries') || 'No waste entries found'}</p>
          </div>
        )}
      </div>

      {/* Record Waste Modal */}
      {showRecordModal && (
        <RecordWasteModal onClose={() => setShowRecordModal(false)} />
      )}

      {/* Waste Detail Modal */}
      {selectedEntry && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && setSelectedEntry(null)}
        >
          <div className="relative w-full max-w-lg rounded-lg bg-card shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold">{t('waste.details') || 'Waste Entry Details'}</h2>
              <button
                onClick={() => setSelectedEntry(null)}
                className="rounded-full p-1 hover:bg-secondary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Ingredient & Status */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold">{selectedEntry.ingredientName}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedEntry.quantity} {selectedEntry.unit}
                  </p>
                </div>
                {getStatusBadge(selectedEntry.status)}
              </div>

              {/* Category */}
              <div>
                <label className="text-xs text-muted-foreground">{t('waste.category')}</label>
                <div className="mt-1">
                  <span className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${getCategoryColor(selectedEntry.category)}`}>
                    {t(`waste.${selectedEntry.category}`)}
                  </span>
                </div>
              </div>

              {/* Value */}
              <div>
                <label className="text-xs text-muted-foreground">{t('waste.value') || 'Estimated Value'}</label>
                <p className="text-2xl font-bold text-red-600">฿{selectedEntry.estimatedValue.toFixed(2)}</p>
              </div>

              {/* Reason */}
              <div>
                <label className="text-xs text-muted-foreground">{t('inventory.reason')}</label>
                <p className="mt-1 text-sm bg-secondary rounded-lg p-3">{selectedEntry.reason}</p>
              </div>

              {/* Photo */}
              {selectedEntry.photoUrl && (
                <div>
                  <label className="text-xs text-muted-foreground">{t('waste.photo') || 'Photo Evidence'}</label>
                  <a
                    href={selectedEntry.photoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 flex items-center gap-2 text-primary hover:underline"
                  >
                    <Image className="h-4 w-4" />
                    {t('waste.viewPhoto') || 'View Photo'}
                  </a>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t text-sm">
                <div>
                  <label className="text-xs text-muted-foreground">{t('waste.recordedBy') || 'Recorded By'}</label>
                  <p className="font-medium">{selectedEntry.recordedByName}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">{t('common.date')}</label>
                  <p className="font-medium">{new Date(selectedEntry.createdAt).toLocaleString()}</p>
                </div>
                {selectedEntry.approvedByName && (
                  <div>
                    <label className="text-xs text-muted-foreground">{t('waste.approvedBy') || 'Approved By'}</label>
                    <p className="font-medium">{selectedEntry.approvedByName}</p>
                  </div>
                )}
                {selectedEntry.approvedAt && (
                  <div>
                    <label className="text-xs text-muted-foreground">{t('waste.approvedAt') || 'Approved At'}</label>
                    <p className="font-medium">{new Date(selectedEntry.approvedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* Actions for pending entries */}
              {canApprove && selectedEntry.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      handleApprove(selectedEntry._id, true);
                      setSelectedEntry(null);
                    }}
                    className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-white font-medium hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 inline mr-2" />
                    {t('waste.approve')}
                  </button>
                  <button
                    onClick={() => {
                      handleApprove(selectedEntry._id, false);
                      setSelectedEntry(null);
                    }}
                    className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-white font-medium hover:bg-red-700"
                  >
                    <XCircle className="h-4 w-4 inline mr-2" />
                    {t('waste.reject')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WastePage;
